import { useMemo } from "react";

import { flow } from "fp-ts/lib/function";

import { ExclusiveLensStatus, Visibility } from "../../gql-generated/types";
import { type LensFolderRoleTypePermissions, ROLE_ACTIONS } from "../../shared/constants";
import { useSelectedLensFolderPermissions } from "../../shared/hooks/roles";
import {
    filterDistributionVisibilityOptionsBasedOnCurrentVisibility,
    filterDoNotShowToSnapchatVisibilityOption,
    filterVisibilityOptionsByPermissions,
    mergeFilteredVisibilityOptionsWithCurrentLensVisibility,
    sortVisibilityOptions,
} from "../models";

import { makeExclusiveLensVisibilityConfig } from "./utils";
import { type LensVisibilityConfig } from "./visibilityConfigs";

interface LensSubmissionVisibilityInput {
    lens?: {
        visibility: Visibility;
        exclusiveLensStatus: ExclusiveLensStatus;
    } | null;
}

type LensForVisibilityUpdate = NonNullable<LensSubmissionVisibilityInput["lens"]>;

const makeDefaultVisibilityOption = (
    permittedVisibilityOptions: Visibility[],
    lensForUpdateVisiblity?: Visibility,
): Visibility | undefined => {
    if (lensForUpdateVisiblity && permittedVisibilityOptions.includes(lensForUpdateVisiblity)) {
        return lensForUpdateVisiblity;
    }

    if (permittedVisibilityOptions.includes(Visibility.VisibilityPublic)) {
        return Visibility.VisibilityPublic;
    }

    if (permittedVisibilityOptions.includes(Visibility.VisibilityDontPublishToSnapchat)) {
        return Visibility.VisibilityDontPublishToSnapchat;
    }

    const fallbackVisibility = permittedVisibilityOptions[0];

    return fallbackVisibility;
};

/**
 * Builds the role-filtered visibility choices used by Lens Pro and Web Lens publication flows.
 * An update always keeps its current visibility available, even when the role cannot change it.
 */
export const makeLensSubmissionVisibilityConfig = (
    { ACTIONS, VISIBILITY_OPTIONS = [] }: LensFolderRoleTypePermissions,
    lensForUpdate?: LensForVisibilityUpdate | null,
): LensVisibilityConfig => {
    const lensForUpdateVisibility = lensForUpdate?.visibility;
    const isPermittedToUpdateLensVisibility = ACTIONS.includes(ROLE_ACTIONS.UPDATE_LENS_VISIBILITY);
    const isExclusiveLens =
        lensForUpdate?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive ||
        lensForUpdate?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusEligible;

    const options = flow(
        filterVisibilityOptionsByPermissions(VISIBILITY_OPTIONS),
        filterDoNotShowToSnapchatVisibilityOption(lensForUpdateVisibility), // once lens is published, can't revert back
        filterDistributionVisibilityOptionsBasedOnCurrentVisibility(lensForUpdateVisibility), // can't distribute not published lens from the update flow
        mergeFilteredVisibilityOptionsWithCurrentLensVisibility(lensForUpdateVisibility), // include back lens for update visibility - keep last in the pipeline, insert above ^
        sortVisibilityOptions,
    )([
        Visibility.VisibilityDontPublishToSnapchat,
        Visibility.VisibilityHidden,
        Visibility.VisibilityOffline,
        Visibility.VisibilityPublic,
    ]);

    const visibilityConfig: LensVisibilityConfig = {
        disabled: !isPermittedToUpdateLensVisibility || options.length === 1,
        defaultVisibility: makeDefaultVisibilityOption(options, lensForUpdateVisibility),
        options,
        disabledOptions: {},
        communityDistributionOptions: options.filter((option) => option !== Visibility.VisibilityDontPublishToSnapchat),
    };

    return isExclusiveLens ? makeExclusiveLensVisibilityConfig(visibilityConfig) : visibilityConfig;
};

export const useLensSubmissionVisibilityConfig = (
    lensForUpdate?: LensSubmissionVisibilityInput,
): LensVisibilityConfig => {
    const {
        selectedLensFolderPermissions: { ACTIONS, VISIBILITY_OPTIONS = [] },
    } = useSelectedLensFolderPermissions();

    return useMemo(
        () => makeLensSubmissionVisibilityConfig({ ACTIONS, VISIBILITY_OPTIONS }, lensForUpdate?.lens),
        [ACTIONS, VISIBILITY_OPTIONS, lensForUpdate?.lens],
    );
};
